import EnvConfigHelper from '../../../../../../helpers/envConfigHelper';
import TaskPromise from '../../../../../../services/TaskPromise';
import { HTTPDataService } from '../../../../../../services/HTTPDataService';
import { AssistantResponse, AssistantResponseType, AssistantsResponse, IAssistantsServiceTypes, AssistantVersionResponse, RevisionResponse, GetAssistantType, GetAssistantsType, CreateAssistantType, UpdateAssistantType, RemoveAssistantType, GetAssistantVersionsType, CreateAssistantVersionType, GetAssistantVersionType, UpdateAssistantVersionType, RemoveAssistantVersionType, PublishLiveAssistantVersionType, CreateAssistantVersionRevisionType as CreateAssistantVersionDraftType, UpdateAssistantVersionRevisionType, GetAssistantVersionRevisionType, PublishBetaAssistantType as PublishBetaAssistantVersionType, UpdateAssistantVersionToolsType, UpdateAssistantVersionDocumentsType, UpdateAssistantAudienceType, GetAssistantVersionRevisionsType, CreateAssistantEvaluationType, CreateAssistantEvaluationResponse, GetAssistantEvaluationsType, UpdateAssistantEvaluationType, RemoveAssistantEvaluationType, CopyAssistantEvaluationType, CopyAssistantEvaluationResponse, GetAssistantEvaluationCollectionType, AssistantsEvaluationCollectionResponse, CreateAssistantEvaluationManualEntryType, UpdateAssistantEvaluationManualEntryType, CreateAssistantsEvaluationManualEntryResponse, UpdateAssistantsEvaluationManualEntryResponse, DeleteAssistantEvaluationManualEntryType, CreateCollectionEvaluationRunType, GetCollectionAssistantEvaluationRunsType, DeleteCollectionAssistantEvaluationRuns, AssistantVersionRevisionRunResponse, GetCollectionAssistantEvaluationRuns, ExportAssistantEvaluationEntriesCsvType, ImportAssistantEvaluationEntriesCsvType, ImportEvaluationEntriesCsvResponse, AssistantVersionsResponse, AssistantVersionType, RevisionBaseTypesResponse, RevisionBaseType, AssistantEvaluationsResponse, AssistantVersionRevisionRunsResponse } from '../types/AssistantsServiceTypes';
import EventHandler from '../../../../../../services/EventHandler';
import { AssistantEvent } from '../../../../../../models/BackendEvents';
import ServiceBackendEventsHelper from '../../../../../../services/helpers/ServiceBackendEventsHelper';
import { IBackendEventsService } from '../../../../../../services/IBackendEventsService';

const baseUri = 'api/admin/assistant';

class AssistantsService extends HTTPDataService implements IAssistantsServiceTypes {
    baseURL = EnvConfigHelper.get('api-base-url', '');
    backendEventsHelper: ServiceBackendEventsHelper;
    constructor(public getAccessToken: () => Promise<string | undefined>, backendEvents: IBackendEventsService) {
        super();
        this.backendEventsHelper = new ServiceBackendEventsHelper(this.events, backendEvents);
    }

    getAssistants = (_: GetAssistantsType) => {
        const uri = baseUri;
        return this.get<AssistantsResponse>(uri);
    };
    createAssistant = async (body: CreateAssistantType) => {
        const uri = baseUri;
        const result = await this.post<AssistantResponse>(uri, undefined, body);
        // TEMP Workaround for cache busting delay in BE
        await TaskPromise.delay(5000);
        return result;
    };
    getAssistant = ({ assistantId }: GetAssistantType) => {
        const uri = `${baseUri}/${assistantId}`;
        return this.get<AssistantResponse>(uri);
    };
    updateAssistant = ({ assistantId, action, body }: UpdateAssistantType) => {
        const uri = `${baseUri}/${assistantId}`;
        return this.patch<AssistantResponseType>(uri, { action }, body);
    };
    removeAssistant = ({ assistantId }: RemoveAssistantType) => {
        const uri = `${baseUri}/${assistantId}`;
        return this.delete<boolean>(uri);
    };

    getAssistantVersions = ({ assistantId }: GetAssistantVersionsType) => {
        const uri = `${baseUri}/${assistantId}/version`;
        return this.getObjectArray<AssistantVersionsResponse, AssistantVersionType>(uri, 'versions');
    };
    createAssistantVersion = ({ assistantId, body }: CreateAssistantVersionType) => {
        const uri = `${baseUri}/${assistantId}/version`;
        return this.post<AssistantVersionResponse>(uri, undefined, body);
    };
    getAssistantVersion = ({ assistantId, versionNumber }: GetAssistantVersionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}`;
        return this.get<AssistantVersionResponse>(uri);
    };
    updateAssistantVersion = ({ assistantId, versionNumber, action, body }: UpdateAssistantVersionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}`;
        return this.patch<AssistantVersionResponse>(uri, { action }, body);
    };
    removeAssistantVersion = ({ assistantId, versionNumber }: RemoveAssistantVersionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}`;
        return this.delete<boolean>(uri);
    };

    publishLiveAssistantVersion = ({ assistantId, versionNumber, stage }: PublishLiveAssistantVersionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/publish-live`;
        return this.post<AssistantVersionResponse>(uri, { stage: stage });
    };
    publishBetaAssistantVersion = ({ assistantId, versionNumber }: PublishBetaAssistantVersionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/publish-beta`;
        return this.post<AssistantVersionResponse>(uri);
    };

    createAssistantVersionDraft = ({ assistantId, versionNumber }: CreateAssistantVersionDraftType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/create-draft`;
        return this.post<AssistantVersionResponse>(uri);
    };


    /**
     * @param revisionKey '_' for latest revision
     */
    getAssistantVersionRevisions = ({ assistantId, versionNumber }: GetAssistantVersionRevisionsType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/revision`;
        return this.getObjectArray<RevisionBaseTypesResponse, RevisionBaseType>(uri, 'revisions');
    };
    getAssistantVersionRevision = ({ assistantId, versionNumber, revisionKey = '_', }: GetAssistantVersionRevisionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/revision/${revisionKey}`;
        return this.get<RevisionResponse>(uri);
    };
    updateAssistantVersionRevision = ({ assistantId, versionNumber, body, action }: UpdateAssistantVersionRevisionType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/revision`;
        return this.patch<RevisionResponse>(uri, { action }, body);
    };


    updateAssistantVersionTools = ({ action, assistantId, versionNumber, body }: UpdateAssistantVersionToolsType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionNumber}/revision/tools`;
        return this.patch<AssistantVersionResponse>(uri, { action }, body);
    };
    updateAssistantVersionDocuments = ({ action, assistantId, body }: UpdateAssistantVersionDocumentsType) => {
        const uri = `${baseUri}/${action}/version/${assistantId}/revision/documents`;
        return this.patch<AssistantVersionResponse>(uri, { action }, body);
    };


    updateAssistantAudience = ({ action, assistantId, body }: UpdateAssistantAudienceType) => {
        const uri = `${baseUri}/${assistantId}/audience`;
        return this.patch<AssistantResponse>(uri, { action }, body);
    };

    //#region evaluation
    createAssistantEvaluation = ({ assistantId, body }: CreateAssistantEvaluationType) => {
        const uri = `${baseUri}/${assistantId}/evaluation`;
        return this.post<CreateAssistantEvaluationResponse>(uri, undefined, body);
    };
    getAssistantEvaluations = ({ assistantId }: GetAssistantEvaluationsType) => {
        const uri = `${baseUri}/${assistantId}/evaluation`;
        return this.getObjectArray<AssistantEvaluationsResponse, CreateAssistantEvaluationResponse>(uri, 'evaluations');
    };
    copyAssistantEvaluation = ({ assistantId, evaluationKey, body }: CopyAssistantEvaluationType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}/copy`;
        return this.post<CopyAssistantEvaluationResponse>(uri, undefined, body);
    };
    updateAssistantEvaluation = ({ assistantId, evaluationKey, body }: UpdateAssistantEvaluationType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}`;
        return this.patch<CreateAssistantEvaluationResponse>(uri, undefined, body);
    };
    removeAssistantEvaluation = ({ assistantId, evaluationKey }: RemoveAssistantEvaluationType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}`;
        return this.delete<CreateAssistantEvaluationResponse>(uri, undefined);
    };
    getAssistantEvaluationCollection = ({ assistantId, evaluationKey }: GetAssistantEvaluationCollectionType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}`;
        return this.get<AssistantsEvaluationCollectionResponse>(uri);
    };

    createAssistantEvaluationManualEntry = ({ assistantId, evaluationKey, body }: CreateAssistantEvaluationManualEntryType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}/entry`;
        return this.post<CreateAssistantsEvaluationManualEntryResponse>(uri, undefined, body);
    };
    updateAssistantEvaluationManualEntry = ({ assistantId, evaluationKey, entryKey, body }: UpdateAssistantEvaluationManualEntryType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}/entry/${entryKey}`;
        return this.patch<UpdateAssistantsEvaluationManualEntryResponse>(uri, undefined, body);
    };
    deleteAssistantEvaluationManualEntry = ({ assistantId, evaluationKey, entryKey }: DeleteAssistantEvaluationManualEntryType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}/entry/${entryKey}`;
        return this.delete<boolean>(uri);
    };

    createCollectionEvaluationRun = async ({ assistantId, versionKey, revisionKey, evaluationKey, description, evaluation_strictness_key }: CreateCollectionEvaluationRunType) => {
        const uri = `${baseUri}/${assistantId}/version/${versionKey}/revision/${revisionKey}/evaluation/${evaluationKey}/run`;
        const desc = typeof description === 'string' ? description.trim() : undefined;
        const query = {
            ...(desc ? { description: desc } : {}),
            ...(evaluation_strictness_key ? { evaluation_strictness_key } : {}),
        };
        const response = await this.post<AssistantVersionRevisionRunResponse>(uri, Object.keys(query).length ? query : undefined);
        return response;
    };
    getCollectionAssistantEvaluationRuns = ({ assistantId, versionKey, revisionKey }: GetCollectionAssistantEvaluationRunsType) => {
        const query = {
            ...(versionKey ? { versionKey } : {}),
            ...(revisionKey ? { revisionKey } : {}),
        };
        const uri = `${baseUri}/${assistantId}/evaluation-run`;
        return this.getObjectArray<AssistantVersionRevisionRunsResponse, AssistantVersionRevisionRunResponse>(uri, 'runs', query);
    };

    getCollectionAssistantEvaluationRun = async ({ assistantId, evaluationRunKey }: GetCollectionAssistantEvaluationRuns) => {
        const uri = `${baseUri}/${assistantId}/evaluation-run/${evaluationRunKey}`;
        return await this.get<AssistantVersionRevisionRunResponse>(uri);
    };
    deleteCollectionAssistantEvaluationRun = async ({ assistantId, evaluationRunKey }: DeleteCollectionAssistantEvaluationRuns) => {
        const uri = `${baseUri}/${assistantId}/evaluation-run/${evaluationRunKey}`;
        const response = this.delete<boolean>(uri);
        return response;
    };

    exportAssistantEvaluationEntriesCsv = async (params: ExportAssistantEvaluationEntriesCsvType): Promise<boolean> => {
        const { assistantId, evaluationKeys, downloadFilename } = params;
        let allSuccess = true;
        for (const key of evaluationKeys) {
            const uri = `${baseUri}/${assistantId}/evaluation/${key}/entry/export/csv`;
            const success = await this.download(uri, 'text/csv', downloadFilename);
            if (!success) {
                allSuccess = false;
            }
        }
        return allSuccess;
    };

    importAssistantEvaluationEntriesCsv = async ({ assistantId, evaluationKey, file, onUploadProgress }: ImportAssistantEvaluationEntriesCsvType) => {
        const uri = `${baseUri}/${assistantId}/evaluation/${evaluationKey}/entry/import/csv`;
        const formData = new FormData();
        formData.append('file', file);
        const result = await this.post<ImportEvaluationEntriesCsvResponse>(uri, undefined, formData as any, onUploadProgress);
        return result;
    };

    events = {
        assistant: new EventHandler<AssistantEvent>()
    };

    bindEvents = () => {
        this.backendEventsHelper.bind();
    };
    unbindEvents = () => {
        this.backendEventsHelper.unbind();
    };
    //#endregion
}

export default AssistantsService;



// type UpdateRequestBody = Pick<DeepPartial<AssistantResponse['data']>, 'config' | 'listing' | 'audience' | 'publishing'>;
// type UpdateRequest = { [P in keyof UpdateRequestBody]: UpdateRequestBody[P] };
// export const update = (models: UpdateRequest) => Object.entries(models).map(([part, data]) => ({ data: { part, ...data } }));
